'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { Medal } from '@/types/achievements';
import ImageAdaptive from '@/components/ImageAdaptive';

export default function AwardMedalNotification() {
    const [pendingAwardMedals, setPendingAwardMedals] = useState<Medal[]>([]);
    const [currentMedalIndex, setCurrentMedalIndex] = useState(0);
    const setUserScore = useAuthStore((s) => s.setUserScore);
    const { handleError } = useHandleError();

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch(`/users/me/achievement/award/medal`, {
                    method: 'POST',
                });
                setPendingAwardMedals(data?.award_medals);
            } catch (error: unknown) {
                handleError(error);
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const scoreData = await apiFetch(`/users/my_score`);
                setUserScore({
                    achievementScore: scoreData.achievement_score,
                    totalMedalScore: scoreData.total_medal_score,
                    totalBadgeScore: scoreData.total_badge_score,
                    gearPostCount: scoreData.gear_post_count,
                    gearLikeCount: scoreData.gear_like_count,
                    festaEntryCheckinCount: scoreData.festa_entry_checkin_count,
                    totalScore: scoreData.total_score,
                    ownedLabCount: scoreData.owned_lab_count,
                    editorLaunchCount: scoreData.editor_launch_count,
                    whiteTeacherAnswerCount: scoreData.white_teacher_answer_count,
                    editorWriteCount: scoreData.editor_write_count,
                });
            } catch (error: unknown) {
                handleError(error);
            }
        })();
    }, [pendingAwardMedals]);

    const handleNextMedal = () => {
        if (currentMedalIndex < pendingAwardMedals.length - 1) {
            setCurrentMedalIndex(currentMedalIndex + 1);
        }
    };

    const handlePreviousMedal = () => {
        if (currentMedalIndex > 0) {
            setCurrentMedalIndex(currentMedalIndex - 1);
        }
    };

    const currentMedal = pendingAwardMedals[currentMedalIndex];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, delay: 0.5 }}
            className="flex flex-col justify-center items-center px-1 lg:px-5 w-full h-full"
        >
            <div className="relative flex justify-center items-center w-full h-full">
                <div className="flex w-[50px] justify-end px-2">
                    {currentMedalIndex > 0 ? (
                        <button onClick={handlePreviousMedal}>
                            <ImageAdaptive src={`/images/achievement/back.svg`} baseWidth={30} />
                        </button>
                    ) : (
                        <ImageAdaptive src={`/images/achievement/back-disabled.svg`} baseWidth={30} />
                    )}
                </div>
                <div className="flex flex-col w-full justify-center items-center">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.0, delay: 0.0 }}
                        className="font-corp font-bold text-xl lg:text-3xl flex items-center justify-center text-center pb-4"
                    >
                        ムサコレ・メダル獲得！
                    </motion.div>
                    {currentMedal && (
                        <>
                            <div className="font-notojp font-medium text-xs flex flex-col items-center justify-center text-center">
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 1.0, delay: 1.5 }}
                                    className="flex justify-center items-center w-full lg:w-[250px]"
                                >
                                    <ImageAdaptive
                                        src={`/images/achievement/medal/frame/medal-award-frame.svg`}
                                        baseWidth={250}
                                        className="relative"
                                    />
                                    <img
                                        src={currentMedal?.medal_resource_url}
                                        // className="absolute top-[25px] lg:top-[25px] left-[153px] lg:left-[153px] w-[250px]"
                                        className="absolute w-[250px] ml-2"
                                    />
                                </motion.div>
                                <div className="flex flex-col flex-grow justify-center items-center w-full">
                                    <div className="grid grid-cols-2 grid-rows-1 gap-4 w-full h-full">
                                        <div className="col-span-2 row-span-2 w-full h-full flex justify-center items-center mt-2 mb-8">
                                            <ImageAdaptive
                                                src={`/images/achievement/medal/medal-${currentMedal.achievement_score}.svg`}
                                                baseWidth={50}
                                            />
                                            <div className="flex flex-col pl-0 lg:pl-4">
                                                <div className="font-corp font-bold text-base lg:text-xl text-color-base">
                                                    {currentMedal.medal_name}
                                                </div>
                                                <div className="font-notojp font-medium text-xs text-color-base pt-1">
                                                    {currentMedal.description}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="w-full flex flex-row">
                                        <div className="w-[120px] h-full flex justify-center items-center">
                                            <div className="flex flex-col lg:flex-row justify-center lg:items-center font-corp font-bold text-sm text-color-base w-full lg:h-[50px]">
                                                <div className="text-xs lg:text-sm">獲得スコア</div>
                                                <div className="font-montserrat font-medium text-base lg:text-xl text-color-c-orange pl-0 lg:pl-4">
                                                    {currentMedal.achievement_score}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="h-full flex flex-grow justify-center items-center">
                                            <div className="flex flex-col lg:flex-row justify-center lg:items-center font-corp font-bold text-color-base w-full lg:h-[50px]">
                                                <div className="text-xs lg:text-sm">獲得日</div>
                                                <div className="font-montserrat font-medium text-xs tracking-widest text-color-base pl-0 lg:pl-4 lg:pt-0 pt-1">
                                                    {currentMedal.award_date_time}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div className="flex w-[50px] justify-start px-2">
                    {currentMedalIndex < pendingAwardMedals.length - 1 ? (
                        <button onClick={handleNextMedal}>
                            <ImageAdaptive src={`/images/achievement/next.svg`} baseWidth={30} />
                        </button>
                    ) : (
                        <ImageAdaptive src={`/images/achievement/next-disabled.svg`} baseWidth={30} />
                    )}
                </div>
            </div>
        </motion.div>
    );
}
